import { ErrorMessage, Field, Form, Formik } from "formik";
import { Bank } from "@phosphor-icons/react";
import { FC, useMemo } from "react";
import * as yup from "yup";
import { motion } from "framer-motion";
import useVerifyNIN from "../hooks/useVerifyNIN";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import { leaveSetupTask } from "../utils/setupTaskReturn";

const schema = yup.object().shape({
  nin_number: yup
    .string()
    .required("NIN is required")
    .length(11, "Must be 11 Digits"),
});

const VerifyNIN: FC<{}> = () => {
  const { refetch } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromSetup = (location.state as { fromSetup?: boolean } | null)?.fromSetup;
  const { isVerifyingNINError, verifyNIN, verifyingNIN, verifyingNINError } =
    useVerifyNIN({
      onSuccess() {
        if (fromSetup) {
          leaveSetupTask(navigate, location.state);
        } else {
          navigate(-1);
        }
        toast.success("NIN Verified Successfully");
        refetch();
      },
      onError() {
        toast.error("An error occurred while verifying NIN");
      },
    });

  const isLoading = useMemo(() => verifyingNIN, [verifyingNIN]);

  return (
    <motion.div
      initial={{
        opacity: 0,
      }}
      animate={{
        opacity: 1,
      }}
      exit={{
        opacity: 0,
      }}
      className="fixed top-0 left-0 bg-deepBlue/80 backdrop-blur-sm w-full h-screen grid place-items-center p-5"
    >
      <Formik
        validationSchema={schema}
        initialValues={{
          nin_number: "",
        }}
        onSubmit={(values, { resetForm }) => {
          verifyNIN(values, {
            onSuccess() {
              resetForm();
            },
          });
        }}
      >
        {({ isValid, dirty }) => {
          return (
            <Form className="bg-white max-w-sm w-full p-5 rounded-xl shadow-sm flex items-center flex-col gap-5">
              <div className="p-2 bg-gray-100 rounded-full ring-8 ring-gray-50">
                <Bank size={28} />
              </div>
              <div className="flex items-center flex-col gap-2">
                <div className="font-medium text-xl">Verify NIN</div>
                <p className="text-gray-500 text-center">
                  Kindly provide us with your National Identity Number to
                  continue this process.
                </p>
              </div>
              <div className="flex flex-col gap-2 max-w-md w-full">
                <label
                  htmlFor="pin"
                  className="text-sm font-bold text-gray-700"
                >
                  NIN
                </label>
                <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
                  <Field
                    autoComplete="off"
                    type="text"
                    id="nin_number"
                    name="nin_number"
                    placeholder="Enter NIN"
                    className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
                  />
                </div>
                <ErrorMessage
                  name="nin_number"
                  component={motion.div}
                  className="text-red-500"
                />
              </div>
              {isVerifyingNINError && (
                <div className="text-red-500">{verifyingNINError?.message}</div>
              )}
              <div className="flex items-center gap-5 w-full">
                <button
                  type="button"
                  aria-label="Cancel"
                  onClick={() => navigate(-1)}
                  className="p-3 px-5 flex-1 border-red-100 border-2 rounded-lg font-bold bg-red-50/50 text-red-500 text-sm"
                >
                  Cancel
                </button>
                <button
                  disabled={isLoading || !isValid || !dirty}
                  className="p-3 px-5 flex-1 border-visaro-gray-700 border-2 rounded-lg font-bold bg-deepBlue text-white text-sm"
                >
                  {isLoading ? "Verifying" : "Verify"}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </motion.div>
  );
};

export default VerifyNIN;
